import { createApi } from '@reduxjs/toolkit/query/react';
import { invalidateAdminTags } from 'api/AdminAccessApi';
import { baseQueryWithReAuth } from 'api/Api';
import { LifeReviewResponse } from 'api/LifeReviewApi';
import { FileMessage, Message } from 'api/MessagesApi';
import { User, UserAccessLevelPlanEnum } from 'api/UsersApi';

export interface SysAdminReport {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  amountTotal: string;
}

export interface SysAdminPayment {
  id: string;
  planName: string;
  couponName: string;
  stripePaymentIntentId: string;
  stripeCreated: Date;
  stripeAmountTotal: string;
  user: User;
  affiliateUser: User;
}

export interface SysAdminGift {
  id: string;
  priceId: string;
  recipientName: string;
  recipientEmail: string;
  sendDate: string;
  senderEmail: string;
  senderName: string;
  senderText: string;
  createdAt: Date;
}

export interface SysAdminPatientProfile {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: User['status'] | null;
  portalAccessStatus?: 'registered' | 'invite_sent' | 'profile_only' | null;
  messagesCount?: number | null;
  messagesPublishedCount?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SysAdminUser extends User {
  messagesCount: number;
  messagesPublishedCount: number;
  portalAccessStatus?: 'registered' | 'invite_sent' | 'profile_only' | null;
  adminUsers: User[];
  userAdmins: User[];
  isOrganizationSponsoredAccess?: boolean | null;
  effectiveAccessLevelPlan?: UserAccessLevelPlanEnum | null;
}

export interface SysAdminUserListItem {
  id: string;
  userId?: string | null;
  patientProfileId?: string | null;
  recordType?: 'registered_user' | 'patient_profile' | null;
  portalAccessStatus?: 'registered' | 'invite_sent' | 'profile_only' | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  phone?: string | null;
  lifecycleStatus?: User['status'] | null;
  status?: User['status'] | null;
  accessLevelPlan?: User['accessLevelPlan'] | null;
  affiliateCode?: string | null;
  messagesCount?: number | null;
  messagesPublishedCount?: number | null;
  adminUsers?: (User | null)[] | null;
  userAdmins?: (User | null)[] | null;
  patientProfile?: SysAdminPatientProfile | null;
}

export interface SysAdminPatientProfileDetails {
  recordType?: 'patient_profile' | 'registered_user' | null;
  patientProfileId: string;
  userId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  portalAccessStatus?: 'registered' | 'invite_sent' | 'profile_only' | null;
  lifecycleStatus?: User['status'] | null;
  messagesCount?: number | null;
  messagesPublishedCount?: number | null;
  lifeReviewResponsesCount?: number | null;
  lifeReviewResponses?: LifeReviewResponse[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SysAdminPromoteToDeceasedResult {
  usersPromoted: number;
  patientProfilesPromoted: number;
}

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizeSysAdminUserListItem = (item: SysAdminUserListItem): SysAdminUserListItem => {
  const rawItem = item as SysAdminUserListItem & {
    publishedMessagesCount?: number | string | null;
    totalMessagesCount?: number | string | null;
    messages_published_count?: number | string | null;
    messages_count?: number | string | null;
  };

  const rawPatientProfile = item.patientProfile as
    | (SysAdminPatientProfile & {
        publishedMessagesCount?: number | string | null;
        totalMessagesCount?: number | string | null;
        messages_published_count?: number | string | null;
        messages_count?: number | string | null;
      })
    | null
    | undefined;

  const messagesPublishedCount =
    toNullableNumber(item.messagesPublishedCount) ??
    toNullableNumber(rawItem.publishedMessagesCount) ??
    toNullableNumber(rawItem.messages_published_count) ??
    toNullableNumber(rawPatientProfile?.messagesPublishedCount) ??
    toNullableNumber(rawPatientProfile?.publishedMessagesCount) ??
    toNullableNumber(rawPatientProfile?.messages_published_count);

  const messagesCount =
    toNullableNumber(item.messagesCount) ??
    toNullableNumber(rawItem.totalMessagesCount) ??
    toNullableNumber(rawItem.messages_count) ??
    toNullableNumber(rawPatientProfile?.messagesCount) ??
    toNullableNumber(rawPatientProfile?.totalMessagesCount) ??
    toNullableNumber(rawPatientProfile?.messages_count);

  return {
    ...item,
    messagesPublishedCount: messagesPublishedCount ?? 0,
    messagesCount: messagesCount ?? 0
  };
};

const normalizeSysAdminPatientProfileDetails = (item: SysAdminPatientProfileDetails): SysAdminPatientProfileDetails => {
  const rawItem = item as SysAdminPatientProfileDetails & {
    id?: string;
    status?: User['status'] | null;
    publishedMessagesCount?: number | string | null;
    totalMessagesCount?: number | string | null;
    lifeReviewCount?: number | string | null;
    life_review_count?: number | string | null;
    messages_published_count?: number | string | null;
    messages_count?: number | string | null;
  };

  const rawUser = (rawItem as { user?: (Partial<SysAdminUser> & { phone?: string | null }) | null }).user;
  const rawPatientProfile = (
    rawItem as {
      patientProfile?: (Partial<SysAdminPatientProfileDetails> & Partial<SysAdminPatientProfile> & { id?: string | null }) | null;
    }
  ).patientProfile;

  const messagesPublishedCount =
    toNullableNumber(rawItem.messagesPublishedCount) ??
    toNullableNumber(rawItem.publishedMessagesCount) ??
    toNullableNumber(rawItem.messages_published_count) ??
    toNullableNumber(rawPatientProfile?.messagesPublishedCount) ??
    toNullableNumber(rawUser?.messagesPublishedCount);

  const messagesCount =
    toNullableNumber(rawItem.messagesCount) ??
    toNullableNumber(rawItem.totalMessagesCount) ??
    toNullableNumber(rawItem.messages_count) ??
    toNullableNumber(rawPatientProfile?.messagesCount) ??
    toNullableNumber(rawUser?.messagesCount);

  const lifeReviewResponsesCount =
    toNullableNumber(rawItem.lifeReviewResponsesCount) ??
    toNullableNumber(rawItem.lifeReviewCount) ??
    toNullableNumber(rawItem.life_review_count) ??
    toNullableNumber(rawPatientProfile?.lifeReviewResponsesCount);

  return {
    ...item,
    patientProfileId: item.patientProfileId || rawPatientProfile?.patientProfileId || rawPatientProfile?.id || rawItem.id || '',
    userId: item.userId || rawPatientProfile?.userId || rawUser?.id || null,
    firstName: item.firstName ?? rawPatientProfile?.firstName ?? rawUser?.firstName ?? null,
    lastName: item.lastName ?? rawPatientProfile?.lastName ?? rawUser?.lastName ?? null,
    displayName: item.displayName ?? rawPatientProfile?.displayName ?? null,
    email: item.email ?? rawPatientProfile?.email ?? rawUser?.email ?? null,
    phone: item.phone ?? rawPatientProfile?.phone ?? rawUser?.phone ?? null,
    portalAccessStatus: item.portalAccessStatus ?? rawPatientProfile?.portalAccessStatus ?? rawUser?.portalAccessStatus ?? null,
    lifecycleStatus: item.lifecycleStatus ?? rawItem.status ?? rawPatientProfile?.lifecycleStatus ?? rawPatientProfile?.status ?? rawUser?.status ?? null,
    messagesPublishedCount: messagesPublishedCount ?? 0,
    messagesCount: messagesCount ?? 0,
    lifeReviewResponsesCount: lifeReviewResponsesCount ?? 0
  };
};

const mapUserListItemToPatientProfileDetails = (item: SysAdminUserListItem): SysAdminPatientProfileDetails => {
  const profile = item.patientProfile;

  return normalizeSysAdminPatientProfileDetails({
    recordType: item.recordType,
    patientProfileId: item.patientProfileId || profile?.id || item.id,
    userId: item.userId || null,
    firstName: item.firstName ?? profile?.firstName ?? null,
    lastName: item.lastName ?? profile?.lastName ?? null,
    displayName: item.displayName ?? null,
    email: item.email ?? profile?.email ?? null,
    phone: item.phone ?? profile?.phone ?? null,
    portalAccessStatus: item.portalAccessStatus ?? profile?.portalAccessStatus ?? null,
    lifecycleStatus: item.lifecycleStatus ?? item.status ?? profile?.status ?? null,
    messagesCount: item.messagesCount ?? profile?.messagesCount ?? 0,
    messagesPublishedCount: item.messagesPublishedCount ?? profile?.messagesPublishedCount ?? 0,
    lifeReviewResponsesCount: 0,
    createdAt: profile?.createdAt ?? null,
    updatedAt: profile?.updatedAt ?? null
  });
};

const isNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const status = (error as { status?: number | string }).status;
  return status === 404 || status === '404';
};

export const sysAdminApi = createApi({
  baseQuery: baseQueryWithReAuth,
  reducerPath: 'sysAdminApi',
  tagTypes: [
    'SysAdminUsers',
    'SysAdminPayments',
    'SysAdminMessages',
    'SysAdminLifeReviewResponses',
    'SysAdminMessageFiles',
    'SysAdminReport',
    'SysAdminGifts'
  ],
  endpoints: (build) => ({
    getAllUsersSysAdmin: build.query<{ list: SysAdminUserListItem[]; total: number }, { limit?: number; offset?: number }>({
      query: (params) => ({ url: '/sysadmin/users', params }),
      transformResponse: (response: { data: [SysAdminUserListItem[], number] }) => ({
        list: response.data[0].map(normalizeSysAdminUserListItem),
        total: response.data[1]
      }),
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'SysAdminUsers' as const,
                id
              })),
              { type: 'SysAdminUsers', id: 'LIST' }
            ]
          : [{ type: 'SysAdminUsers', id: 'LIST' }]
    }),
    getOneUserSysAdmin: build.query<SysAdminUser, Pick<SysAdminUser, 'id'>>({
      query: ({ id }) => ({ url: `/sysadmin/users/${id}` }),
      transformResponse: (response: { data: SysAdminUser }) => response.data,
      providesTags: (result, error, { id }) => [{ type: 'SysAdminUsers', id }]
    }),
    getOnePatientProfileSysAdmin: build.query<SysAdminPatientProfileDetails, { patientProfileId: string }>({
      async queryFn({ patientProfileId }, _queryApi, _extraOptions, fetchWithBQ) {
        const detailsResult = await fetchWithBQ({ url: `/sysadmin/patient-profiles/${patientProfileId}` });

        if (!detailsResult.error) {
          return {
            data: normalizeSysAdminPatientProfileDetails((detailsResult.data as { data: SysAdminPatientProfileDetails }).data)
          };
        }

        if (!isNotFoundError(detailsResult.error)) {
          return { error: detailsResult.error };
        }

        const fallbackListResult = await fetchWithBQ({
          url: '/sysadmin/users',
          params: { limit: 1, offset: 0, patientProfileId }
        });

        if (fallbackListResult.error) {
          return { error: fallbackListResult.error };
        }

        const fallbackData = (fallbackListResult.data as { data: [SysAdminUserListItem[], number] }).data;
        const firstItem = fallbackData?.[0]?.[0];

        if (!firstItem) {
          return { error: detailsResult.error };
        }

        return {
          data: mapUserListItemToPatientProfileDetails(normalizeSysAdminUserListItem(firstItem))
        };
      },
      providesTags: (result, error, { patientProfileId }) => [{ type: 'SysAdminUsers', id: patientProfileId }]
    }),
    getAllPaymentsSysAdmin: build.query<
      { list: SysAdminPayment[]; total: number },
      { limit?: number; offset?: number; userId?: string; affiliateUserId?: string; fromDate?: string; toDate?: string }
    >({
      query: (params) => ({ url: '/sysadmin/payments', params }),
      transformResponse: (response: { data: [SysAdminPayment[], number] }) => ({
        list: response.data[0],
        total: response.data[1]
      }),
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'SysAdminPayments' as const,
                id
              })),
              { type: 'SysAdminPayments', id: 'LIST' }
            ]
          : [{ type: 'SysAdminPayments', id: 'LIST' }]
    }),
    getAllMessagesSysAdmin: build.query<
      { list: Message[]; total: number },
      { limit?: number; offset?: number; userId?: string; patientProfileId?: string }
    >({
      query: (params) => ({ url: `/sysadmin/messages`, params }),
      transformResponse: (response: { data: [Message[], number] }) => ({
        list: response.data[0],
        total: response.data[1]
      }),
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'SysAdminMessages' as const,
                id
              })),
              { type: 'SysAdminMessages', id: 'LIST' }
            ]
          : [{ type: 'SysAdminMessages', id: 'LIST' }]
    }),
    getOneMessageSysAdmin: build.query<Message, Pick<Message, 'id'>>({
      query: ({ id }) => ({ url: `/sysadmin/messages/${id}` }),
      transformResponse: (response: { data: Message }) => response.data,
      providesTags: (result, error, { id }) => [{ type: 'SysAdminMessages', id }]
    }),
    removeSysAdminMessage: build.mutation<boolean, { messageId: string }>({
      query: ({ messageId }) => ({
        url: `/sysadmin/messages/${messageId}`,
        method: 'DELETE'
      }),
      transformResponse: (response: { success?: boolean; data?: { deleted?: boolean } }) => response.success || !!response.data?.deleted,
      invalidatesTags: () => [{ type: 'SysAdminMessages', id: 'LIST' }, { type: 'SysAdminUsers', id: 'LIST' }]
    }),
    getFileMessageSysAdmin: build.query<FileMessage, Pick<Message, 'id'>>({
      query: ({ id }) => ({ url: `/sysadmin/messages/${id}/file` }),
      transformResponse: (response: { data: FileMessage }) => response.data,
      providesTags: (result, error, { id }) => [{ type: 'SysAdminMessageFiles', id }]
    }),
    getAllLifeReviewResponsesSysAdmin: build.query<
      { list: LifeReviewResponse[]; total: number },
      { limit?: number; offset?: number; userId?: string; patientProfileId?: string }
    >({
      async queryFn(params, _queryApi, _extraOptions, fetchWithBQ) {
        const candidateUrls = ['/sysadmin/life-review-responses', '/sysadmin/life-review/responses', '/sysadmin/life-reviews/responses'];

        let firstError: unknown = null;

        for (const url of candidateUrls) {
          const result = await fetchWithBQ({ url, params });

          if (!result.error) {
            const response = result.data as { data: [LifeReviewResponse[], number] };
            return {
              data: {
                list: response.data[0],
                total: response.data[1]
              }
            };
          }

          if (!firstError) {
            firstError = result.error;
          }

          if (!isNotFoundError(result.error)) {
            return { error: result.error };
          }
        }

        return { error: firstError as { status: number; data: unknown } };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'SysAdminLifeReviewResponses' as const,
                id: id || 'unknown'
              })),
              { type: 'SysAdminLifeReviewResponses', id: 'LIST' }
            ]
          : [{ type: 'SysAdminLifeReviewResponses', id: 'LIST' }]
    }),
    removeSysAdminLifeReviewResponse: build.mutation<boolean, { responseId: string }>({
      query: ({ responseId }) => ({
        url: `/sysadmin/life-review-responses/${responseId}`,
        method: 'DELETE'
      }),
      transformResponse: (response: { success?: boolean; data?: { deleted?: boolean } }) => response.success || !!response.data?.deleted,
      invalidatesTags: () => [{ type: 'SysAdminLifeReviewResponses', id: 'LIST' }]
    }),
    updateUserSysAdmin: build.mutation<boolean, { userId: string; accessLevelPlan: UserAccessLevelPlanEnum }>({
      query: ({ userId, ...body }) => ({
        url: `/sysadmin/users/${userId}`,
        method: 'POST',
        body
      }),
      transformResponse: (response: { success: boolean }) => response.success,
      invalidatesTags: (result, error, { userId }) => [
        userId ? { type: 'SysAdminUsers', id: userId } : { type: 'SysAdminUsers', id: 'LIST' }
      ]
    }),
    updateUserAffiliateSysAdmin: build.mutation<boolean, { userId: string; affiliateCode: string }>({
      query: ({ userId, ...body }) => ({
        url: `/sysadmin/users/${userId}/affiliate`,
        method: 'POST',
        body
      }),
      transformResponse: (response: { success: boolean }) => response.success,
      invalidatesTags: (result, error, { userId }) => [
        userId ? { type: 'SysAdminUsers', id: userId } : { type: 'SysAdminUsers', id: 'LIST' }
      ]
    }),
    // createPaymentSysAdmin: build.mutation<SysAdminPayment, { userId: string }>({
    //   query: (body) => ({
    //     url: '/sysadmin/payments',
    //     method: 'POST',
    //     body
    //   }),
    //   transformResponse: (response: { data: SysAdminPayment }) => response.data,
    //   invalidatesTags: () => [
    //     { type: 'SysAdminPayments', id: 'LIST' },
    //     { type: 'SysAdminUsers', id: 'LIST' }
    //   ],
    //   async onQueryStarted(_, { dispatch, queryFulfilled }) {
    //     try {
    //       await queryFulfilled;
    //       dispatch(invalidateAuthTags());
    //     } catch (e) {}
    //   }
    // }),
    removeSysAdminUser: build.mutation<boolean, Pick<SysAdminUser, 'id'>>({
      query: ({ id }) => ({
        url: `/sysadmin/users/${id}`,
        method: 'DELETE'
      }),
      transformResponse: (response: { data: { deleted: boolean } }) => response.data.deleted,
      invalidatesTags: () => [{ type: 'SysAdminUsers', id: 'LIST' }]
    }),
    removeSysAdminPatientProfile: build.mutation<boolean, { patientProfileId: string }>({
      query: ({ patientProfileId }) => ({
        url: `/sysadmin/patient-profiles/${patientProfileId}`,
        method: 'DELETE'
      }),
      transformResponse: (response: { data: { deleted: boolean } }) => response.data.deleted,
      invalidatesTags: () => [{ type: 'SysAdminUsers', id: 'LIST' }]
    }),
    getReportSysAdmin: build.query<
      { list: SysAdminReport[]; total: number },
      { limit?: number; offset?: number; fromDate: string; toDate: string; userId?: string }
    >({
      query: (params) => ({ url: '/sysadmin/report', params }),
      transformResponse: (response: { data: [SysAdminReport[], number] }) => ({
        list: response.data[0],
        total: response.data[1]
      }),
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'SysAdminReport' as const,
                id
              })),
              { type: 'SysAdminReport', id: 'LIST' }
            ]
          : [{ type: 'SysAdminReport', id: 'LIST' }]
    }),
    promoteToDeceasedSysAdmin: build.mutation<SysAdminPromoteToDeceasedResult, {}>({
      query: () => ({
        method: 'POST',
        url: `/sysadmin/qa/promote-deceased`
      }),
      transformResponse: (response: {
        success?: boolean;
        data?: {
          usersPromoted?: number | null;
          patientProfilesPromoted?: number | null;
        };
      }) => {
        if (response.success !== true) {
          throw new Error('promoteToDeceasedSysAdmin failed');
        }

        return {
          usersPromoted: response.data?.usersPromoted ?? 0,
          patientProfilesPromoted: response.data?.patientProfilesPromoted ?? 0
        };
      },
      invalidatesTags: () => [{ type: 'SysAdminUsers', id: 'LIST' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAdminTags());
        } catch (e) {}
      }
    }),
    deliveryMessagesSysAdmin: build.mutation<boolean, {}>({
      query: () => ({
        method: 'POST',
        url: `/sysadmin/qa/delivery-deceased`
      }),
      transformResponse: (response: { success: boolean }) => response.success
    }),
    getAllGiftSysAdmin: build.query<{ list: SysAdminGift[]; total: number }, { limit?: number; offset?: number }>({
      query: (params) => ({ url: '/sysadmin/gifts', params }),
      transformResponse: (response: { data: [SysAdminGift[], number] }) => ({
        list: response.data[0],
        total: response.data[1]
      }),
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'SysAdminGifts' as const,
                id
              })),
              { type: 'SysAdminGifts', id: 'LIST' }
            ]
          : [{ type: 'SysAdminGifts', id: 'LIST' }]
    })
  })
});

export const {
  useGetAllMessagesSysAdminQuery,
  useRemoveSysAdminMessageMutation,
  useGetOneMessageSysAdminQuery,
  useGetFileMessageSysAdminQuery,
  useGetAllLifeReviewResponsesSysAdminQuery,
  useRemoveSysAdminLifeReviewResponseMutation,
  useGetAllUsersSysAdminQuery,
  useGetOneUserSysAdminQuery,
  useGetOnePatientProfileSysAdminQuery,
  useUpdateUserAffiliateSysAdminMutation,
  useGetAllPaymentsSysAdminQuery,
  useUpdateUserSysAdminMutation,
  useRemoveSysAdminUserMutation,
  useRemoveSysAdminPatientProfileMutation,
  useGetReportSysAdminQuery,
  useDeliveryMessagesSysAdminMutation,
  usePromoteToDeceasedSysAdminMutation,
  useGetAllGiftSysAdminQuery
} = sysAdminApi;
